import { useDispatch } from 'react-redux'
import { Bell, Menu, Search, Settings } from 'lucide-react'
import { UserButton, OrganizationSwitcher } from '@clerk/clerk-react'
import { useNavigate } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'

import { toggleSidebar } from '@/store/slices/uiSlice'
import { useGetAlertsQuery } from '@/store/api'

export default function Header() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  
  // Fetch alerts to get unread count
  const { data: alertsData } = useGetAlertsQuery({ 
    limit: 100, 
    sortBy: 'created_at', // Use snake_case for database column
    sortOrder: 'desc' 
  })
  
  // Calculate unread alerts count
  const alerts = alertsData?.data || []
  const unreadAlerts = alerts.filter(alert => 
    alert.status === 'new' || alert.priority === 'critical'
  ).length

  const handleSettingsClick = () => {
    navigate('/settings')
  }

  return (
    <header className="bg-white border-b border-gray-200 px-4 lg:px-6 h-16 flex items-center justify-between sticky top-0 z-40 shadow-sm">
      {/* Left side */}
      <div className="flex items-center space-x-4">
        {/* Mobile menu button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => dispatch(toggleSidebar())}
          className="lg:hidden"
        >
          <Menu className="h-5 w-5" />
        </Button>

        {/* Desktop sidebar toggle */}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => dispatch(toggleSidebar())}
          className="hidden lg:flex"
        >
          <Menu className="h-5 w-5" />
        </Button>

        {/* Search */}
        <div className="hidden md:flex relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search alerts, equipment, work orders..."
            className="pl-10 w-96"
          />
        </div>
      </div>

      {/* Right side */}
      <div className="flex items-center space-x-3">
        {/* Mobile search */}
        <Button variant="ghost" size="icon" className="md:hidden">
          <Search className="h-5 w-5" />
        </Button>

        {/* Organization Switcher */}
        <div className="hidden md:block">
          <OrganizationSwitcher
            appearance={{
              elements: {
                organizationSwitcherTrigger: "px-3 py-2 text-sm font-medium text-gray-700 hover:text-trust-blue hover:bg-gray-50 rounded-md border border-gray-200",
                organizationSwitcherPopoverCard: "shadow-lg border border-gray-200 rounded-lg font-sans",
                organizationSwitcherPopoverActions: "font-sans",
                organizationSwitcherPopoverActionButton: "text-gray-700 hover:text-trust-blue hover:bg-gray-50 font-medium font-sans",
                organizationPreview: "font-sans",
                organizationPreviewTextContainer: "font-sans",
                organizationPreviewMainIdentifier: "font-medium font-sans",
                organizationPreviewSecondaryIdentifier: "text-gray-600 font-sans",
              },
              variables: {
                colorPrimary: "#2563EB",
                colorText: "#1F2937",
                colorTextSecondary: "#6B7280",
                borderRadius: "0.5rem",
                fontFamily: "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
              },
            }}
          />
        </div>

        {/* Notifications */}
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          {unreadAlerts > 0 && (
            <Badge className="absolute -top-1 -right-1 h-5 w-5 rounded-full text-xs bg-critical-red text-white flex items-center justify-center">
              {unreadAlerts > 9 ? '9+' : unreadAlerts}
            </Badge>
          )}
        </Button>

        {/* Settings button */}
        <Button 
          variant="ghost" 
          size="icon"
          onClick={handleSettingsClick}
          title="Settings"
        >
          <Settings className="h-5 w-5" />
        </Button>

        {/* Clerk UserButton */}
        <UserButton
          userProfileMode="navigation"
          userProfileUrl="/profile"
          appearance={{
            elements: {
              avatarBox: "h-10 w-10",
              userButtonPopoverCard: "shadow-lg border border-gray-200 rounded-lg font-sans",
              userButtonPopoverActions: "font-sans",
              userButtonPopoverActionButton: "text-gray-700 hover:text-trust-blue hover:bg-gray-50 font-medium font-sans",
              userButtonPopoverActionButtonText: "font-sans",
              userButtonPopoverFooter: "font-sans",
              userButtonPopoverFooterButton: "text-gray-600 hover:text-gray-900 font-sans",
              userPreview: "font-sans",
              userPreviewTextContainer: "font-sans",
              userPreviewMainIdentifier: "font-medium font-sans",
              userPreviewSecondaryIdentifier: "text-gray-600 font-sans",
            },
            variables: {
              colorPrimary: "#2563EB",
              colorText: "#1F2937",
              colorTextSecondary: "#6B7280",
              borderRadius: "0.5rem",
              fontFamily: "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
            },
          }}
        />
      </div>
    </header>
  )
}